// use-archive-session.ts — Sessions-list suite, action.
//
// Toggles `archivedAt` on a session. Returns the updated session.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session } from '@cabal/shared/domain/session/types.js';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { sessionsListKeys } from './keys';

export interface ArchiveSessionVars {
  sessionId: string;
  archive: boolean;
}

export function useArchiveSession(): ActionResult<ArchiveSessionVars, Session> {
  const api = useApi();
  const apiSessions = api.sessions;
  const queryClient = useQueryClient();

  return useActionHook<ArchiveSessionVars, Session>(
    useMemo(
      () => ({
        mutationFn: async ({ sessionId, archive }) => {
          const session = await apiSessions.byId(sessionId).update({
            body: { archivedAt: archive ? new Date().toISOString() : null },
          });
          if (!session) {
            throw new Error('Session archive update returned no session.');
          }
          return session;
        },
        onInvalidate: () => {
          queryClient.invalidateQueries({ queryKey: sessionsListKeys.all });
        },
      }),
      [apiSessions, queryClient],
    ),
  );
}
